import React, { createContext, useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { useForm, UseFormReturn, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Filter as FilterRule, FilterGroup, FilterOperator, Comparator } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { Input } from '@/components/ui/input';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Filter as FilterIcon, RotateCcw, X, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Types
export type FilterValues = Record<string, string | Date | undefined>;

interface FilterFieldMeta {
  field: string;
  comparator: Comparator;
  type: 'text' | 'date' | 'select' | 'toggle';
}

interface FilterFormContextValue {
  form: UseFormReturn<FilterValues>;
  registerField: (name: string, meta: FilterFieldMeta) => void;
  unregisterField: (name: string) => void;
  fieldMetas: Map<string, FilterFieldMeta>;
}

const FilterFormContext = createContext<FilterFormContextValue | null>(null);

function useFilterFormContext() {
  const context = useContext(FilterFormContext);
  if (!context) {
    throw new Error('FilterField must be used within a FilterForm');
  }
  return context;
}

// FilterForm component
export interface FilterFormProps {
  children: React.ReactNode;
  defaultValues?: FilterValues;
  onFiltersChange?: (params: {
    filterOperator: FilterOperator;
    filterFilterGroups: FilterGroup[];
    values: FilterValues;
  }) => void;
  debounceMs?: number;
}

export function FilterForm({
  children,
  defaultValues = {},
  onFiltersChange,
  debounceMs = 300,
}: FilterFormProps) {
  const form = useForm<FilterValues>({
    defaultValues,
  });

  const fieldMetasRef = React.useRef<Map<string, FilterFieldMeta>>(new Map());
  const [, forceUpdate] = React.useState({});

  const registerField = useCallback((name: string, meta: FilterFieldMeta) => {
    fieldMetasRef.current.set(name, meta);
    forceUpdate({});
  }, []);

  const unregisterField = useCallback((name: string) => {
    fieldMetasRef.current.delete(name);
    forceUpdate({});
  }, []);

  const watchedValues = form.watch();
  const debouncedValues = useDebounce(watchedValues, debounceMs);
  const isDirty = form.formState.isDirty;

  // Build filter groups from values and field metas
  const buildFilterGroups = useCallback((values: FilterValues): FilterGroup[] => {
    const filters: FilterRule[] = [];

    fieldMetasRef.current.forEach((meta, name) => {
      const val = values[name];
      if (val === undefined || val === '') return;
      const value = meta.type === 'date' && val instanceof Date ? val.toISOString() : String(val);
      filters.push({ field: meta.field, value, action: meta.comparator });
    });

    return filters.length ? [{ operator: FilterOperator.And, filters }] : [];
  }, []);

  // Only call onFiltersChange after the user has interacted at least once.
  // We track "has interacted" separately from isDirty because react-hook-form
  // resets isDirty when a value returns to its default, which would block
  // legitimate filter changes (e.g. selecting "Active" after "Any").
  const lastEmittedRef = React.useRef<string>('');
  const hasInteractedRef = React.useRef(false);

  useEffect(() => {
    if (!isDirty && !hasInteractedRef.current) return;
    if (isDirty) hasInteractedRef.current = true;

    const serialized = JSON.stringify(debouncedValues);
    if (serialized === lastEmittedRef.current) return;
    lastEmittedRef.current = serialized;

    if (onFiltersChange) {
      const filterGroups = buildFilterGroups(debouncedValues);
      onFiltersChange({
        filterOperator: FilterOperator.And,
        filterFilterGroups: filterGroups,
        values: debouncedValues,
      });
    }
  }, [debouncedValues, isDirty, onFiltersChange, buildFilterGroups]);

  const contextValue = useMemo(
    () => ({
      form,
      registerField,
      unregisterField,
      fieldMetas: fieldMetasRef.current,
    }),
    [form, registerField, unregisterField]
  );

  return <FilterFormContext.Provider value={contextValue}>{children}</FilterFormContext.Provider>;
}

// FilterField base props
interface FilterFieldBaseProps {
  name: string;
  label: string;
  field: string;
  comparator: Comparator;
  placeholder?: string;
  className?: string;
}

// Text field
export interface FilterTextFieldProps extends FilterFieldBaseProps {
  type: 'text';
  isSearch?: boolean;
}

// Date field
export interface FilterDateFieldProps extends FilterFieldBaseProps {
  type: 'date';
}

// Select field
export interface FilterSelectFieldProps extends FilterFieldBaseProps {
  type: 'select';
  options: { label: React.ReactNode; value: string }[];
  /** Default value to show when no value is selected (defaults to first option if not specified) */
  defaultValue?: string;
}

export interface FilterToggleFieldProps extends FilterFieldBaseProps {
  type: 'toggle';
  options: { label: React.ReactNode; value: string }[];
}

export type FilterFieldProps =
  | FilterTextFieldProps
  | FilterDateFieldProps
  | FilterSelectFieldProps
  | FilterToggleFieldProps;

export function FilterField(props: FilterFieldProps) {
  const { form, registerField, unregisterField } = useFilterFormContext();
  const { name, label, field, comparator, placeholder, className, type } = props;

  // Register field on mount, unregister on unmount
  useEffect(() => {
    registerField(name, { field, comparator, type });
    return () => unregisterField(name);
  }, [name, field, comparator, type, registerField, unregisterField]);

  return (
    <div className={className}>
      <Label htmlFor={name}>{label}</Label>
      <Controller
        name={name}
        control={form.control}
        render={({ field: formField }) => {
          if (type === 'text') {
            const textProps = props as FilterTextFieldProps;
            return (
              <Input
                id={name}
                type="text"
                placeholder={placeholder}
                value={(formField.value as string) || ''}
                onChange={(e) => formField.onChange(e.target.value)}
                isSearch={textProps.isSearch}
              />
            );
          }

          if (type === 'date') {
            return (
              <DateTimePicker
                value={formField.value as Date | undefined}
                onChange={(d) => formField.onChange(d)}
                placeholder={placeholder || 'Select date'}
                granularity="day"
              />
            );
          }

          // Toggle type
          if (type === 'toggle') {
            const toggleProps = props as FilterToggleFieldProps;
            const currentValue = (formField.value as string) ?? '';

            return (
              <div className="flex flex-wrap gap-2 py-2">
                {toggleProps.options.map((opt) => {
                  const isActive = currentValue === opt.value;
                  const isTextLabel = typeof opt.label === 'string';
                  return (
                    <Button
                      key={opt.value}
                      variant="outline"
                      size="sm"
                      className={cn(
                        'flex h-7 min-w-12 items-center gap-2 rounded-full border text-xs font-semibold transition-all',
                        isTextLabel ? 'px-3' : 'px-0',
                        isActive
                          ? 'border-lime-400/60 bg-lime-500/15 text-white shadow-[0_0_0_1px_rgba(190,242,100,0.35)]'
                          : 'border-white/10 bg-white/5 text-white opacity-60 hover:border-white/20 hover:bg-white/10 hover:opacity-100'
                      )}
                      onClick={() => {
                        // Toggle behavior: clicking selected pill deselects it
                        const newValue = currentValue === opt.value ? '' : opt.value;
                        formField.onChange(newValue);
                      }}
                      type="button"
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
            );
          }

          // Select type
          const selectProps = props as FilterSelectFieldProps;
          // Use defaultValue, or first option value as fallback when no value is set
          const effectiveValue =
            (formField.value as string) ||
            selectProps.defaultValue ||
            selectProps.options[0]?.value ||
            '';
          return (
            <Select value={effectiveValue} onValueChange={(val) => formField.onChange(val)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {selectProps.options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value || ' '} disabled={opt.value === ' '}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }}
      />
    </div>
  );
}

// Helper components for common patterns
export function FilterClearButton({ className }: { className?: string }) {
  const { form, fieldMetas } = useFilterFormContext();

  const handleClear = () => {
    const emptyValues: FilterValues = {};
    fieldMetas.forEach((_: FilterFieldMeta, name: string) => {
      emptyValues[name] = undefined;
    });
    form.reset(emptyValues, { keepDirty: true });
  };

  return (
    <Button onClick={handleClear} variant="ghost" size="sm" className={className}>
      Clear
    </Button>
  );
}

export function FilterInlineResetButton({
  onReset,
  className,
}: {
  onReset?: () => void;
  className?: string;
}) {
  const { form, fieldMetas } = useFilterFormContext();
  const watchedValues = form.watch();

  const hasValues = useMemo(
    () => Object.values(watchedValues).some((v) => v !== undefined && v !== ''),
    [watchedValues]
  );

  const handleReset = () => {
    if (!hasValues && !form.formState.isDirty) return;
    const emptyValues: FilterValues = {};
    fieldMetas.forEach((_: FilterFieldMeta, name: string) => {
      emptyValues[name] = undefined;
    });
    form.reset(emptyValues);
    onReset?.();
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className={cn(
        'w-full text-sm text-lime/80',
        !hasValues && !form.formState.isDirty && 'opacity-50 cursor-not-allowed',
        className
      )}
      disabled={!hasValues && !form.formState.isDirty}
      onClick={handleReset}
    >
      Reset filters
    </Button>
  );
}

// Filter header with title and close button
export interface FilterHeaderProps {
  onClose?: () => void;
  className?: string;
}

export function FilterHeader({ onClose, className }: FilterHeaderProps) {
  const activeFilterCount = useActiveFilterCount();

  return (
    <div
      className={cn(
        'sticky top-0 z-20 flex h-[48px] items-center justify-between border-b bg-background px-4',
        className
      )}
    >
      <span className="text-sm font-medium">Filters</span>
      {onClose && (
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="ml-auto flex items-center gap-2"
        >
          <FilterIcon className="h-4 w-4" />
          <span className="flex items-center gap-1">
            Hide
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-green/40 px-1.5 text-xs font-semibold text-white">
                {activeFilterCount}
              </span>
            )}
          </span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// Reset filters button to be placed in filter content area
export interface FilterResetButtonProps {
  onReset?: () => void;
  className?: string;
}

export function FilterResetButton({ onReset, className }: FilterResetButtonProps) {
  const { form, fieldMetas } = useFilterFormContext();

  const handleReset = () => {
    const emptyValues: FilterValues = {};
    fieldMetas.forEach((_: FilterFieldMeta, name: string) => {
      emptyValues[name] = undefined;
    });
    form.reset(emptyValues);
    // Notify parent to clear URL params/state
    onReset?.();
  };

  return (
    <Button
      onClick={handleReset}
      variant="outline"
      size="sm"
      className={cn('w-full justify-start', className)}
    >
      <RotateCcw className="mr-2 h-4 w-4" />
      Reset filters
    </Button>
  );
}

// Hook to access form context from custom components
export function useFilterForm() {
  return useFilterFormContext();
}

// Count active filters
export function useActiveFilterCount() {
  const { form } = useFilterFormContext();
  const values = form.watch();

  return useMemo(() => {
    return Object.values(values).filter((v) => v !== undefined && v !== '').length;
  }, [values]);
}

// Filter section with collapsible accordion
export interface FilterSectionProps {
  /** Section title displayed in the header */
  title: string;
  /** Child filter fields */
  children: React.ReactNode;
  /** Whether the section is open by default */
  defaultOpen?: boolean;
  /** Optional className for the content container */
  className?: string;
}

export function FilterSection({
  title,
  children,
  defaultOpen = true,
  className,
}: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between border-b bg-muted/30 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/50">
        <span>{title}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className={cn('grid grid-cols-1 gap-4 p-4', className)}>{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}
